import { LitElement, html, nothing } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { consume } from '@lit/context'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { rmaSubmitModal } from './submit-modal.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { WINDOW_LOCATION, WindowLocation } from '@cloudsuite/mosaic-shared/scripts/window-location.js'
import { type CSEModal } from '@cloudsuite/mosaic-elements'
import { draftRmaContext, submitRmaContext, NotFetchedYet } from 'components/ordering/rma/components/context/rma-context.js'
import { RmaDraftDTO } from 'components/ordering/rma/api/dtos/rma-draft-dto.js'
import { RmaError } from 'components/ordering/rma/api/dtos/error.js'

const translations = {
  header: () => t('header', { ns: 'csc-rma-submit-modal', default: 'Submit return request' }),
  body: () => t('body', { ns: 'csc-rma-submit-modal', default: 'Your return request will be processed, you will hear from us soon. Are you sure you want to submit the request?' }),
  cancel: () => t('cancel', { ns: 'csc-rma-submit-modal', default: 'No, cancel' }),
  confirm: () => t('confirm', { ns: 'csc-rma-submit-modal', default: 'Yes, continue' }),
  error: () => t('error', { ns: 'csc-rma-submit-modal', default: 'Submitting your return request failed. Please try again.' }),
}

@customElement('csc-rma-submit-modal')
export class CSCRmaSubmitModal extends LitElement {
  static override styles = [hostStyle, rmaSubmitModal]

  @query('cse-modal')
  private modal!: CSEModal

  @property({ attribute: 'list-url', type: String })
  public listUrl = ''

  @inject(WINDOW_LOCATION)
  private windowLocation: WindowLocation | undefined

  @consume({ context: draftRmaContext, subscribe: true })
  private draftRma: RmaDraftDTO | null | NotFetchedYet = new NotFetchedYet()

  @consume({ context: submitRmaContext, subscribe: true })
  private submitRma: ((rmaId: string) => Promise<void>) | null = null

  @state() private submitting = false
  @state() private error: string | ReturnType<typeof t> | null = null

  private get draft(): RmaDraftDTO | null {
    if (this.draftRma instanceof NotFetchedYet) return null
    return this.draftRma
  }

  public open() {
    this.modal?.open()
  }

  public close() {
    this.modal?.close()
  }

  private async onConfirm() {
    if (!this.draft || !this.submitRma) return
    this.submitting = true
    this.error = null
    try {
      await this.submitRma(this.draft.id)
      if (this.listUrl) {
        this.windowLocation?.redirect(new URL(this.listUrl, window.location.origin))
      }
    } catch (error) {
      this.error = error instanceof RmaError ? error.message : translations.error()
    } finally {
      this.submitting = false
    }
  }

  override render() {
    return html`
      <cse-modal id="submit-modal" size="default" variant="success">
        <div slot="header">${translations.header()}</div>
        <cse-icon slot="icon" icon-name="check" icon-style="light" size="xl"></cse-icon>
        <cse-text>${translations.body()}</cse-text>
        ${this.error ? html`<cse-text role="alert" class="rma-submit-error">${this.error}</cse-text>` : nothing}
        <cse-button
          slot="footer"
          size="small"
          fit="fill"
          ?disabled="${this.submitting}"
          @click="${() => this.close()}"
        >
          ${translations.cancel()}
        </cse-button>
        <cse-button
          slot="footer"
          size="small"
          fit="fill"
          ?disabled="${this.submitting}"
          @click="${() => this.onConfirm()}"
        >
          ${translations.confirm()}
        </cse-button>
      </cse-modal>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-rma-submit-modal': CSCRmaSubmitModal
  }
}
